/**
 * Open parent menus so a curated help target can mount before we spotlight it.
 * Used when Settings lives under the profile avatar (new sidebar).
 */

import { HELP_TARGET_REQUIRES } from './helpTargetProbe';
import {
  isHelpTargetId,
  resolveHelpTargetElement,
  type HelpTargetId,
} from './helpTargets';

const sleep = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const clickHelpTarget = (id: HelpTargetId): boolean => {
  const el = resolveHelpTargetElement(id);
  if (!el) return false;
  el.click();
  return true;
};

/**
 * If ``target`` needs a parent menu open, click that parent and wait briefly
 * for the child to appear. Returns whether the target is present afterward.
 */
export const ensureHelpTargetVisible = async (
  target: string,
  options?: { attempts?: number; delayMs?: number }
): Promise<boolean> => {
  if (!isHelpTargetId(target)) return false;
  if (resolveHelpTargetElement(target)) return true;

  const parents = HELP_TARGET_REQUIRES[target] ?? [];
  if (parents.length === 0) return false;

  for (const parent of parents) {
    if (!resolveHelpTargetElement(parent)) continue;
    // Already open enough that child exists? (race)
    if (resolveHelpTargetElement(target)) return true;
    clickHelpTarget(parent);
  }

  const attempts = options?.attempts ?? 8;
  const delayMs = options?.delayMs ?? 80;
  for (let i = 0; i < attempts; i += 1) {
    if (resolveHelpTargetElement(target)) return true;
    await sleep(delayMs);
  }
  return resolveHelpTargetElement(target) != null;
};
